import React from "react"
import PropTypes from "prop-types"
import { Typography, Avatar, Flex, Tooltip } from "antd"
import { ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { Line, Doughnut, Bar, Radar } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import EmptyState from "components/common/EmptyState"
import {
  CHART_TYPES,
  CHART_COLORS,
  DONUT_COLORS,
  AVATAR_COLORS,
  ANNUAL_SUMMARY_COLORS,
  lineChartOptions,
  doughnutOptions,
  barChartOptions
} from "../constants"
import styles from "../style.module.css"

const { Text } = Typography

function WidgetEmptyState({ t }) {
  return (
    <div className={styles.widgetEmptyState}>
      <EmptyState title={t("ai_dashboard.no_data")} description=" " />
    </div>
  )
}

function WidgetInfoIcon({ description }) {
  if (!description) return null
  return (
    <Tooltip title={description} placement="topRight" styles={{ body: { maxWidth: 320 } }}>
      <InfoCircleOutlined className={styles.widgetInfoIcon} />
    </Tooltip>
  )
}

WidgetInfoIcon.propTypes = {
  description: PropTypes.string
}

// ── List ──────────────────────────────────────────────────────────────────────
function renderListWidget(chartTitle, description, items, t) {
  const listItems = items.map((d, i) => {
    const name = d.label ?? t("ai_dashboard.list_item_label", { number: i + 1 })
    return {
      rank: d.rank ?? i + 1,
      name,
      initials: name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase(),
      value: d.value ?? 0,
      color: AVATAR_COLORS[i % AVATAR_COLORS.length]
    }
  })
  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      <Flex vertical gap="var(--spacing-ss)" className={styles.rankList}>
        {listItems.length > 0 ? (
          listItems.map((item) => (
            <Flex key={item.rank} align="center" gap="var(--spacing-xxxs)" className={styles.rankItem}>
              <Text type="secondary" className={styles.rankNum}>
                {item.rank}
              </Text>
              <Avatar size={32} className={styles.rankAvatar} style={{ background: item.color }}>
                {item.initials}
              </Avatar>
              <Text className={styles.rankName}>{item.name}</Text>
              <Text strong>${Number(item.value).toLocaleString("en-US", { minimumFractionDigits: 2 })}</Text>
            </Flex>
          ))
        ) : (
          <WidgetEmptyState t={t} />
        )}
      </Flex>
    </div>
  )
}

// ── Pie / Donut ───────────────────────────────────────────────────────────────
function renderPieWidget(chartTitle, description, chartData, t) {
  const labels = chartData?.labels ?? []
  const values = chartData?.series?.[0]?.data ?? []
  const total = values.reduce((s, v) => s + (v ?? 0), 0)
  const dataset = {
    data: values,
    backgroundColor: labels.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]),
    borderWidth: 0,
    hoverOffset: 4
  }
  const data = { labels, datasets: [dataset] }
  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      <Flex align="center" gap="var(--spacing-m)" className={styles.donutWrapper}>
        <div className={styles.donutChart}>
          {labels.length > 0 ? <Doughnut data={data} options={doughnutOptions} /> : <WidgetEmptyState t={t} />}
        </div>
        <Flex vertical gap={6} className={styles.donutLegend}>
          {labels.map((label, i) => (
            <Flex key={label ?? i} align="center" gap={6} className={styles.legendItem}>
              <div className={styles.legendDot} style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
              <Text className={styles.legendLabel}>{label}</Text>
              <Text className={styles.textSm}>{total > 0 ? Math.round(((values[i] ?? 0) / total) * 100) : 0}%</Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </div>
  )
}

// ── Bar ───────────────────────────────────────────────────────────────────────
function renderBarWidget(chartTitle, description, chartData, isStacked, t) {
  const labels = chartData?.labels ?? []
  const series = Array.isArray(chartData?.series) ? chartData.series : []
  const seriesColors = [ANNUAL_SUMMARY_COLORS.income, ANNUAL_SUMMARY_COLORS.expenses, CHART_COLORS.green, CHART_COLORS.purple]
  const datasets = series.map((s, i) => ({
    label: s.name,
    data: s.data,
    backgroundColor: seriesColors[i] ?? DONUT_COLORS[i % DONUT_COLORS.length],
    borderRadius: 4,
    borderWidth: 0
  }))
  const opts = {
    ...barChartOptions,
    scales: {
      ...barChartOptions.scales,
      x: { ...barChartOptions.scales.x, stacked: isStacked },
      y: { ...barChartOptions.scales.y, stacked: isStacked }
    }
  }
  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      <div className={styles.chartWrapper}>
        {labels.length > 0 ? <Bar data={{ labels, datasets }} options={opts} /> : <WidgetEmptyState t={t} />}
      </div>
    </div>
  )
}

// ── Area ──────────────────────────────────────────────────────────────────────
function renderAreaWidget(chartTitle, description, chartData, t) {
  const labels = chartData?.labels ?? []
  const series = Array.isArray(chartData?.series) ? chartData.series : []
  const datasets = series.map((s, i) => ({
    label: s.name,
    data: s.data,
    borderColor: i === 0 ? CHART_COLORS.blue : CHART_COLORS.amber,
    backgroundColor: i === 0 ? `${CHART_COLORS.blue}26` : `${CHART_COLORS.amber}26`,
    fill: true,
    tension: 0.4,
    pointRadius: labels.length > 30 ? 2 : 4,
    pointBackgroundColor: i === 0 ? CHART_COLORS.blue : CHART_COLORS.amber,
    spanGaps: false
  }))
  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      <div className={styles.chartWrapperMd}>
        {labels.length > 0 ? <Line data={{ labels, datasets }} options={lineChartOptions} /> : <WidgetEmptyState t={t} />}
      </div>
    </div>
  )
}

// ── Funnel ────────────────────────────────────────────────────────────────────
function renderFunnelWidget(chartTitle, description, items, t) {
  const maxVal = Math.max(...items.map((d) => d.value ?? 0), 1)
  const isPercent = (label) => label?.toLowerCase().includes("%") || label?.toLowerCase().includes("rate")
  const barColors = [CHART_COLORS.blue, CHART_COLORS.green, CHART_COLORS.amber, CHART_COLORS.purple]

  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      <Flex vertical gap={6} className={styles.funnelList}>
        {items.length > 0 ? (
          items.map((d, i) => {
            const pct = isPercent(d.label) ? Math.min(d.value ?? 0, 100) : ((d.value ?? 0) / maxVal) * 100
            return (
              <Flex key={d.label ?? i} align="center" gap="var(--spacing-xs)" className={styles.funnelRow}>
                <Text className={styles.funnelLabel}>{d.label}</Text>
                <div className={styles.funnelBarBg}>
                  <div className={styles.funnelBarFill} style={{ width: `${pct}%`, background: barColors[i % barColors.length] }} />
                </div>
                <Text className={styles.funnelValue}>{isPercent(d.label) ? `${d.value}%` : d.value}</Text>
              </Flex>
            )
          })
        ) : (
          <WidgetEmptyState t={t} />
        )}
      </Flex>
    </div>
  )
}

// ── Heatmap ───────────────────────────────────────────────────────────────────
function renderHeatmapWidget(chartTitle, description, chartData, t) {
  const xLabels = chartData?.x_labels ?? []
  const yLabels = chartData?.y_labels ?? []
  const matrix = chartData?.values ?? []
  const allVals = matrix.flat().filter((v) => v > 0)
  const minVal = Math.min(...allVals, 0)
  const maxVal = Math.max(...allVals, 1)

  const getColor = (v) => {
    if (v === 0) return "rgba(221, 228, 255, 0.4)"
    const pct = (v - minVal) / (maxVal - minVal)
    const r = Math.round(197 - pct * (197 - 10))
    const g = Math.round(206 - pct * (206 - 15))
    const b = Math.round(255 - pct * (255 - 94))
    return `rgb(${r},${g},${b})`
  }

  const steps = 5
  const legendSteps = Array.from({ length: steps }, (_, i) => {
    const val = Math.round(minVal + ((maxVal - minVal) * (steps - 1 - i)) / (steps - 1))
    return { val, color: getColor(val) }
  })

  const hasData = yLabels.length > 0 && xLabels.length > 0

  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      {hasData ? (
        <div className={styles.heatmapOuter}>
          <div className={styles.heatmapYAxisLabel}>
            <Text type="secondary" className={styles.heatmapAxisTitle}>
              {t("ai_dashboard.heatmap_y_axis")}
            </Text>
          </div>
          <div className={styles.heatmapGridArea}>
            <div className={styles.heatmapScrollable}>
              {yLabels.map((y, yi) => (
                <div key={y} className={styles.heatmapRow}>
                  <Text type="secondary" className={styles.heatmapRowLabel}>
                    {y}
                  </Text>
                  <div className={styles.heatmapCells}>
                    {xLabels.map((x, xi) => {
                      const val = matrix[yi]?.[xi] ?? 0
                      return (
                        <div key={x} className={styles.heatmapBlock} style={{ background: getColor(val) }} title={`${y} / ${x}: ${val}`} />
                      )
                    })}
                  </div>
                </div>
              ))}
              <div className={styles.heatmapXAxis}>
                <div className={styles.heatmapRowLabelSpacer} />
                <div className={styles.heatmapCells}>
                  {xLabels.map((x) => (
                    <Text key={x} type="secondary" className={styles.heatmapXLabel}>
                      {x}
                    </Text>
                  ))}
                </div>
              </div>
              <Text type="secondary" className={styles.heatmapXAxisTitle}>
                {t("ai_dashboard.heatmap_x_axis")}
              </Text>
            </div>
          </div>
          <div className={styles.heatmapLegend}>
            {legendSteps.map(({ val, color }) => (
              <div key={val} className={styles.heatmapLegendItem}>
                <div className={styles.heatmapLegendSwatch} style={{ background: color }} />
                <Text type="secondary" className={styles.heatmapLegendVal}>
                  {val}
                </Text>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <WidgetEmptyState t={t} />
      )}
    </div>
  )
}

// ── Radar ─────────────────────────────────────────────────────────────────────
function renderRadarWidget(chartTitle, description, chartData, t) {
  const labels = chartData?.labels ?? []
  const series = Array.isArray(chartData?.series) ? chartData.series : []
  const radarColors = [CHART_COLORS.blueAlt, CHART_COLORS.pink, CHART_COLORS.purple, CHART_COLORS.amber]
  const datasets = series.map((s, i) => ({
    label: s.name,
    data: s.data,
    borderColor: radarColors[i % radarColors.length],
    backgroundColor: `${radarColors[i % radarColors.length]}33`,
    pointBackgroundColor: radarColors[i % radarColors.length],
    pointBorderColor: "#fff",
    pointRadius: 4,
    borderWidth: 2
  }))
  const radarOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: { display: false },
      legend: {
        display: true,
        position: "top",
        align: "center",
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 12 } }
      }
    },
    scales: {
      r: {
        grid: { color: CHART_COLORS.border },
        angleLines: { color: CHART_COLORS.border },
        pointLabels: { font: { size: 12 }, color: CHART_COLORS.label },
        ticks: { display: false },
        beginAtZero: true
      }
    }
  }
  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      <div className={styles.chartWrapper}>
        {labels.length > 0 ? <Radar data={{ labels, datasets }} options={radarOpts} /> : <WidgetEmptyState t={t} />}
      </div>
    </div>
  )
}

// ── Legend ────────────────────────────────────────────────────────────────────
function renderLegendWidget(chartTitle, description, chartData, t) {
  const items = chartData?.items ?? []
  const total = chartData?.total ?? 0
  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      {items.length === 0 ? (
        <WidgetEmptyState t={t} />
      ) : (
        <>
          <Text type="secondary" className={styles.textPeriodInfo}>
            {t("ai_dashboard.legend_total")}: ${Number(total).toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </Text>
          <Flex vertical gap="var(--spacing-xs)" className={styles.legendWidgetList}>
            {items.map((item, i) => (
              <Flex key={item.label ?? i} align="center" gap="var(--spacing-xs)" className={styles.legendWidgetItem}>
                <div
                  className={styles.legendWidgetBar}
                  style={{ width: `${item.percent}%`, background: DONUT_COLORS[i % DONUT_COLORS.length] }}
                />
                <div className={styles.legendWidgetDot} style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                <Text className={styles.legendWidgetLabel}>{item.label}</Text>
                <Text type="secondary" className={styles.legendWidgetPct}>
                  {item.percent}%
                </Text>
                <Text strong className={styles.legendWidgetValue}>
                  ${Number(item.value).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </Text>
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </div>
  )
}

// ── Stat ──────────────────────────────────────────────────────────────────────
function renderStatWidget(chartTitle, description, chartData, t) {
  const isEmpty = !chartData || Object.keys(chartData).length === 0
  const mainValue = chartData?.value ?? null
  const unit = chartData?.unit ?? ""
  const pctChange = chartData?.change ?? null
  const isDown = pctChange !== null && pctChange < 0
  let formattedValue = null
  if (mainValue !== null) {
    formattedValue = typeof mainValue === "number" && mainValue % 1 !== 0 ? `${mainValue.toFixed(1)}${unit}` : `${mainValue}${unit}`
  }

  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      {isEmpty || mainValue === null ? (
        <WidgetEmptyState t={t} />
      ) : (
        <>
          <div className={styles.bigValue}>{formattedValue}</div>
          {pctChange !== null && (
            <Text className={isDown ? styles.trendDown : styles.trendUp}>
              {isDown ? <ArrowDownOutlined /> : <ArrowUpOutlined />} {Math.abs(pctChange)}% {t("ai_dashboard.from_previous_period")}
            </Text>
          )}
        </>
      )}
    </div>
  )
}

// ── Default Line ──────────────────────────────────────────────────────────────
function renderLineWidget(chartTitle, description, chartData, t) {
  const labels = chartData?.labels ?? []
  const series = Array.isArray(chartData?.series) ? chartData.series : []
  const total = series[0]?.data?.reduce((s, v) => s + (v ?? 0), 0) ?? 0
  const datasets = series.map((s, i) => ({
    label: s.name,
    data: s.data,
    borderColor: i === 0 ? CHART_COLORS.blue : CHART_COLORS.amber,
    backgroundColor: i === 0 ? `${CHART_COLORS.blue}14` : `${CHART_COLORS.amber}14`,
    fill: true,
    tension: 0.4,
    pointBackgroundColor: i === 0 ? CHART_COLORS.blue : CHART_COLORS.amber,
    pointRadius: labels.length > 30 ? 2 : 4,
    pointHoverRadius: 6,
    spanGaps: false
  }))
  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{chartTitle}</Text>
        <WidgetInfoIcon description={description} />
      </Flex>
      <Text type="secondary" className={styles.textPeriodInfo}>
        ${total.toLocaleString()} {t("ai_dashboard.during_period")}
      </Text>
      <div className={styles.chartWrapperMd}>
        {labels.length > 0 ? <Line data={{ labels, datasets }} options={lineChartOptions} /> : <WidgetEmptyState t={t} />}
      </div>
    </div>
  )
}

// ── GenericChartWidget ────────────────────────────────────────────────────────
export default function GenericChartWidget({ data }) {
  const { t } = useTranslation()
  const chartTitle = data?.chart_title ?? t("ai_dashboard.widget_default_title")
  const chartType = data?.chart_type ?? "line"
  const chartData = data?.chart_data ?? null
  const description = data?.description ?? null

  if (chartType === CHART_TYPES.LEGEND) return renderLegendWidget(chartTitle, description, chartData, t)
  if (chartType === CHART_TYPES.STAT) return renderStatWidget(chartTitle, description, chartData, t)
  if (chartType === CHART_TYPES.LIST) return renderListWidget(chartTitle, description, Array.isArray(chartData) ? chartData : [], t)
  if (chartType === CHART_TYPES.PIE || chartType === CHART_TYPES.DONUT) return renderPieWidget(chartTitle, description, chartData, t)
  if (chartType === CHART_TYPES.BAR || chartType === CHART_TYPES.STACKED_BAR || chartType === CHART_TYPES.STACKED_BAR_ALT)
    return renderBarWidget(chartTitle, description, chartData, chartType !== CHART_TYPES.BAR, t)
  if (chartType === CHART_TYPES.AREA) return renderAreaWidget(chartTitle, description, chartData, t)
  if (chartType === CHART_TYPES.FUNNEL) return renderFunnelWidget(chartTitle, description, Array.isArray(chartData) ? chartData : [], t)
  if (chartType === CHART_TYPES.HEATMAP) return renderHeatmapWidget(chartTitle, description, chartData, t)
  if (chartType === CHART_TYPES.RADAR) return renderRadarWidget(chartTitle, description, chartData, t)

  return renderLineWidget(chartTitle, description, chartData, t)
}

GenericChartWidget.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}
